import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrescriptionRenewalModalStep as PrescriptionRenewalModalStep } from '../../../../../entities/PrescriptionRenewal';
import {
  EnumErrorPrescriptionRenewal,
  ErrorPrescriptionRenewalResponse,
  PrescriptionRenewalResponse,
  useCheckPrescriptionRenewalEligibilityMutation,
  useCreatePrescriptionRenewalMutation
} from '../../../../../services/prescriptionRenewal.service';
import { dynamicCustomEventTracking } from '../../../../../lib/gtm';
import { RootState } from '../../../../../store';
import PrescriptionRenewalModalModel from './model';

type PrescriptionRenewalModalViewModelProps = {
  onClose: () => void;
  onOrderCreated: (order: PrescriptionRenewalResponse) => void;
};

export default function PrescriptionRenewalModalViewModel({
  onClose,
  onOrderCreated
}: PrescriptionRenewalModalViewModelProps) {
  const [step, setStep] = useState<PrescriptionRenewalModalStep>(PrescriptionRenewalModalStep.TERMS);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [description, setDescription] = useState('');
  const [errorSubmit, setErrorSubmit] = useState<ErrorPrescriptionRenewalResponse | null>(null);

  const {
    journeys: { activeJourneys, doneJourneys }
  } = useSelector((state: RootState) => state);

  const doctors = useMemo(
    () => PrescriptionRenewalModalModel.getDoctorsFromJourneys([...activeJourneys, ...doneJourneys]),
    [activeJourneys, doneJourneys]
  );

  const [submitOrder, { isLoading: isSubmitting }] = useCreatePrescriptionRenewalMutation();
  const [checkEligibility, { isLoading: isCheckingEligibility }] = useCheckPrescriptionRenewalEligibilityMutation();

  const resetState = () => {
    setStep(PrescriptionRenewalModalStep.TERMS);
    setTermsAccepted(false);
    setSelectedDoctorId('');
    setDescription('');
    setErrorSubmit(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleToggleAccept = () => {
    setTermsAccepted(prev => !prev);
  };

  const handleContinue = () => {
    dynamicCustomEventTracking.send({ dynamicEventName: 'renovacao_pedido_termos_continuar_mc' });
    checkEligibility()
      .unwrap()
      .then(({ allow }) => {
        if (allow) {
          setStep(PrescriptionRenewalModalStep.ORDER_FORM);
        } else {
          setErrorSubmit({ data: { errorType: EnumErrorPrescriptionRenewal.NO_RECENT_ATTENDANCE, message: '' } });
          setStep(PrescriptionRenewalModalStep.FAILURE);
        }
      })
      .catch((error: ErrorPrescriptionRenewalResponse) => {
        if (error?.data?.errorType) {
          setErrorSubmit(error);
        }
        setStep(PrescriptionRenewalModalStep.FAILURE);
      });
  };

  const handleDoctorChange = (doctorId: string) => {
    setSelectedDoctorId(doctorId);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleSubmit = () => {
    dynamicCustomEventTracking.send({ dynamicEventName: 'renovacao_pedido_enviada_mc' });
    submitOrder({ doctorId: selectedDoctorId, description })
      .unwrap()
      .then(data => {
        onOrderCreated(data);
        setStep(PrescriptionRenewalModalStep.SUCCESS);
      })
      .catch((error: ErrorPrescriptionRenewalResponse) => {
        console.log('Error submitting prescription renewal:', error);
        if (error?.data?.errorType) {
          setErrorSubmit(error);
        }
        setStep(PrescriptionRenewalModalStep.FAILURE);
      });
  };

  return {
    errorSubmit,
    step,
    termsAccepted,
    doctors,
    selectedDoctorId,
    description,
    isSubmitting,
    isCheckingEligibility,
    canSubmitOrder: PrescriptionRenewalModalModel.canSubmitOrderForm({ doctorId: selectedDoctorId, description }),
    handleToggleAccept,
    handleContinue,
    handleDoctorChange,
    handleDescriptionChange,
    handleSubmit,
    handleClose
  };
}
